import { createImageVariantConfig, types as sdkTypes } from '../../../util/sdkLoader';
import { storableError } from '../../../util/errors';
import { addMarketplaceEntities } from '../../../ducks/marketplaceData.duck';
import { parse } from '../../../util/urlHelpers';
import { queryAdminListings } from '../BlogMainPage.duck';

const { UUID } = sdkTypes;

const BASE_INCLUDE = ['images', 'currentStock', 'author', 'author.profileImage'];
// ================ Action types ================ //

export const BLOG_ARTICLE_SHOW_REQUEST = 'app/BlogArticlePage/SHOW_REQUEST';
export const BLOG_ARTICLE_SHOW_SUCCESS = 'app/BlogArticlePage/SHOW_SUCCESS';
export const BLOG_ARTICLE_SHOW_ERROR = 'app/BlogArticlePage/SHOW_ERROR';

// ================ Reducer ================ //

const initialState = {
  id: null,
  showInProgress: false,
  showError: null,
};

const blogArticlePageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case BLOG_ARTICLE_SHOW_REQUEST:
      return { ...state, id: payload.id, showInProgress: true, showError: null };
    case BLOG_ARTICLE_SHOW_SUCCESS:
      return { ...state, showInProgress: false };
    case BLOG_ARTICLE_SHOW_ERROR:
      return { ...state, showInProgress: false, showError: payload };
    default:
      return state;
  }
};

export default blogArticlePageReducer;

// ================ Action creators ================ //

export const blogArticleShowRequest = id => ({
  type: BLOG_ARTICLE_SHOW_REQUEST,
  payload: { id },
});

export const blogArticleShowSuccess = () => ({
  type: BLOG_ARTICLE_SHOW_SUCCESS,
});

export const blogArticleShowError = e => ({
  type: BLOG_ARTICLE_SHOW_ERROR,
  error: true,
  payload: e,
});

// ================ Thunks ================ //

// Fetch a single blog listing with full image variants, independent of ListingPage.duck
export const showBlogListing = (listingId, config) => (dispatch, getState, sdk) => {
  dispatch(blogArticleShowRequest(listingId));

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage || {};
  const aspectRatio = aspectHeight / aspectWidth;
if(!listingId){
  return
}
  const params = {
    id: listingId,
    include: BASE_INCLUDE,
    'fields.image': [
      // Scaled variants for large images
      'variants.scaled-small',
      'variants.scaled-medium',
      'variants.scaled-large',
      'variants.scaled-xlarge',

      // Cropped variants for listing thumbnails
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
      `variants.${variantPrefix}-4x`,
      `variants.${variantPrefix}-6x`,

      // Social media
      'variants.facebook',
      'variants.twitter',

      // Avatars
      'variants.square-small',
      'variants.square-small2x',
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-4x`, 1600, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-6x`, 2400, aspectRatio),
  };

  return sdk.listings
    .show(params)
    .then(response => {
      // Store listing and related entities in marketplace data
      dispatch(addMarketplaceEntities(response));
      dispatch(blogArticleShowSuccess());
      return response;
    })
    .catch(e => {
      dispatch(blogArticleShowError(storableError(e)));
      throw e;
    });
};

// loadData used by routeConfiguration for BlogArticlePage
export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
   const queryParams = parse(search);
    const page = Number(queryParams.page || 1);
  const listingId = new UUID(params.id);

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  const listingQueryParams = {
    ...queryParams,
    page,
    perPage: 6,
    pub_listingType: 'blogs',
    include: BASE_INCLUDE,
   'fields.image': [
        'variants.listing-card',
        'variants.listing-card-2x',
        'variants.listing-card-4x',
        'variants.listing-card-6x',
        'variants.scaled-small',
        'variants.scaled-medium',
        'variants.scaled-large',
        'variants.scaled-xlarge',
        'variants.facebook',
        'variants.twitter',
        'variants.square-small',
        'variants.square-small2x'
      ], 
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-4x`, 1600, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-6x`, 2400, aspectRatio),
    'limit.images': 5,
  };
  // Reuse ListingPage.showListing via showBlogListing
  return dispatch(showBlogListing(listingId, config))
    .then(response => {
      // Use current listing's blogTypePreference when querying related listings.
      const current = response?.data?.data;
      const pref = current?.attributes?.publicData?.blogTypePreference
        || queryParams.lang
        || queryParams.blogLang
        || queryParams.pub_blogTypePreference
        || 'en';
      const relatedParams = { ...listingQueryParams, pub_blogTypePreference: pref };
      return dispatch(queryAdminListings(relatedParams)).then(() => response);
    })
    .catch(e => {
      // Error already stored in state by blogArticleShowError
      return null;
    });
};
